/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	ChangeDetectionStrategy,
	OnInit,
	EventEmitter,
	Input,
	Output,
	OnDestroy,
} from '@angular/core';
import * as appStrings from '@touchplan/strings';
import { Observable, Subject } from 'rxjs';
import { MatStatusIndicatorState } from 'src/app/shared/ui/material/mat-status-indicator-state';
import { map, takeUntil } from 'rxjs/operators';
import { ReportUIState } from '../report/reports.component';

@Component({
	selector: 'report-footer',
	templateUrl: './report-footer.component.html',
	styleUrls: ['./report-footer.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportFooterComponent implements OnInit, OnDestroy {
	@Input() public stateUI$: Observable<ReportUIState>;
	@Input() public reportProgress$: Observable<number>;
	@Input() public disableRunReportButton = false;
	@Input() public downloadUrl: string;

	@Output() public runReport: EventEmitter<boolean> = new EventEmitter();
	@Output() public cancel: EventEmitter<boolean> = new EventEmitter();
	@Output() public viewReport: EventEmitter<boolean> = new EventEmitter();
	@Output() public downloadCsv: EventEmitter<boolean> = new EventEmitter();
	@Output() public clearSettings: EventEmitter<boolean> = new EventEmitter();
	@Output() public editSettings: EventEmitter<boolean> = new EventEmitter();

	public strings = appStrings;
	public runCancelViewReportText$: Observable<string>;

	public stateLoadingIndicator$: Observable<MatStatusIndicatorState>;
	public reportUIState = ReportUIState;

	public stateUISync: ReportUIState;

	private shutdownSubject$ = new Subject<void>();

	constructor() {}

	public ngOnInit(): void {
		this.stateUI$.pipe(takeUntil(this.shutdownSubject$)).subscribe((value) => {
			this.stateUISync = value;
		});

		this.runCancelViewReportText$ = this.stateUI$.pipe(
			takeUntil(this.shutdownSubject$),
			map((state) => {
				switch (state) {
					case ReportUIState.EditingOptions:
						return this.strings.REPORT_RUN_REPORT;
					case ReportUIState.RunningReport:
						return this.strings.UICONTROL_TEXT_CANCEL;
					case ReportUIState.ReadyToViewReport:
						return this.strings.REPORT_VIEW_REPORT;
					case ReportUIState.ReadyToDownload:
						return this.strings.REPORT_DOWNLOAD;
				}
			})
		);

		this.stateLoadingIndicator$ = this.stateUI$.pipe(
			takeUntil(this.shutdownSubject$),
			map((state) => {
				switch (state) {
					case ReportUIState.EditingOptions:
						return MatStatusIndicatorState.NotStarted;
					case ReportUIState.RunningReport:
						return MatStatusIndicatorState.InProgress;
					case ReportUIState.ReadyToViewReport:
						return MatStatusIndicatorState.Success;
					case ReportUIState.ReadyToDownload:
						return MatStatusIndicatorState.Success;
				}
			})
		);
	}

	public runCancelViewReportEvent() {
		switch (this.stateUISync) {
			case ReportUIState.EditingOptions:
				this.runReportEvent();
				break;
			case ReportUIState.RunningReport:
				this.cancelEvent();
				break;
			case ReportUIState.ReadyToViewReport:
				this.viewReportEvent();
				break;
			case ReportUIState.ReadyToDownload:
				this.downloadCsvEvent();
				break;
		}
	}

	public runReportEvent() {
		this.runReport.emit(true);
	}

	public viewReportEvent() {
		this.viewReport.emit(true);
	}

	public downloadCsvEvent() {
		this.downloadCsv.emit(true);
	}

	public cancelEvent() {
		this.cancel.emit(true);
	}

	public clearSettingsEvent() {
		this.clearSettings.emit(true);
	}

	public editSettingsEvent() {
		this.editSettings.emit(true);
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
